import { useModalQueue } from '@carvana/merch-react-hooks';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState
} from 'react';
import { AppliedFilters } from '~/contracts/srp';
import { ModalId, ModalPriorities } from './constants';

export type CreateSavedSearchProps = {
  alertName?: string;
  cvnaid?: string;
};

type Toggler = (isOpen: boolean) => void;

type ContextType = {
  createSavedSearchProps: CreateSavedSearchProps;
  filtersToSave: AppliedFilters;
  isAnyModalOpen: boolean;
  isAddCoApplicantModalOpen: boolean;
  isCreditKarmaPromoModalOpen: boolean;
  isCreateSavedSearchModalOpen: boolean;
  isSaveSearchModalOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  setCreditKarmaPromoModalOpen: Toggler;
  setAddCoApplicantModalOpen: Toggler;
  setCreateSavedSearchOpen: (
    isOpen: boolean,
    payload: CreateSavedSearchProps
  ) => void;
  setSaveSearchModalOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setIsSearchBarFocused: Dispatch<SetStateAction<boolean>>;
  setSearchBarModalOpen: Toggler;
  setSoftPullModalOpen: Toggler;
};

const defaultContext: ContextType = {
  createSavedSearchProps: {
    alertName: '',
    cvnaid: ''
  },
  filtersToSave: {},
  isAnyModalOpen: false,
  isCreditKarmaPromoModalOpen: false,
  isAddCoApplicantModalOpen: false,
  isCreateSavedSearchModalOpen: false,
  isSaveSearchModalOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isSoftPullModalOpen: false,
  setCreditKarmaPromoModalOpen() {},
  setAddCoApplicantModalOpen() {},
  setCreateSavedSearchOpen() {},
  setSaveSearchModalOpen() {},
  setIsSearchBarFocused() {},
  setSearchBarModalOpen() {},
  setSoftPullModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }: React.PropsWithChildren) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);
  const [createSavedSearchProps, setCreateSavedSearchProps] =
    useState<CreateSavedSearchProps>(defaultContext.createSavedSearchProps);
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);

  const {
    setCreditKarmaPromoModalOpen,
    setAddCoApplicantModalOpen,
    setCreateSavedSearchOpen,
    setSaveSearchModalOpen,
    setSearchBarModalOpen,
    setSoftPullModalOpen
  } = useMemo(
    () => ({
      setCreditKarmaPromoModalOpen: generateOpener(ModalId.CkPromo),
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setCreateSavedSearchOpen: generateOpener(ModalId.CreateSavedSearch),
      setSaveSearchModalOpen: generateOpener(ModalId.SaveSearch),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull)
    }),
    [generateOpener]
  );

  const setCreateSavedSearchOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, payload: CreateSavedSearchProps) => {
      setCreateSavedSearchOpen(isOpen);
      setCreateSavedSearchProps(payload);
    },
    [setCreateSavedSearchOpen]
  );

  const setSavedSearchModalOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: AppliedFilters) => {
      setSaveSearchModalOpen(isOpen);
      setFiltersToSave(filters);
    },
    [setSaveSearchModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      createSavedSearchProps: createSavedSearchProps,
      filtersToSave,
      isAnyModalOpen: currentModalId > -1,
      isCreditKarmaPromoModalOpen: currentModalId === ModalId.CkPromo,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isCreateSavedSearchModalOpen:
        currentModalId === ModalId.CreateSavedSearch,
      isSaveSearchModalOpen: currentModalId === ModalId.SaveSearch,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      setCreditKarmaPromoModalOpen,
      setAddCoApplicantModalOpen,
      setCreateSavedSearchOpen: setCreateSavedSearchOpenWithFiltersToSave,
      setIsSearchBarFocused,
      setSaveSearchModalOpen: setSavedSearchModalOpenWithFiltersToSave,
      setSearchBarModalOpen,
      setSoftPullModalOpen
    }),
    [
      createSavedSearchProps,
      currentModalId,
      filtersToSave,
      isSearchBarFocused,
      setCreditKarmaPromoModalOpen,
      setAddCoApplicantModalOpen,
      setCreateSavedSearchOpenWithFiltersToSave,
      setSavedSearchModalOpenWithFiltersToSave,
      setSearchBarModalOpen,
      setSoftPullModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;
